import * as APIModel from '@/api/models';
import { IDropdownOption } from '@/common/EditableDropdown';

export interface IStaticDropdownData<T> {
  map: Record<string, T>;
  options: IDropdownOption[];
}

// Using `unknown` and casting inside for simplicity - all entities must have `idKey` and `labelKey` properties
const buildStaticDropdown = <T extends unknown>(entities: T[], idKey: keyof T, labelKey: keyof T): IStaticDropdownData<T> => {
  const map: Record<string, T> = {};
  const options: IDropdownOption[] = [];

  entities.forEach((item) => {
    // cast to string for ID, string for label
    const id = item[idKey] as string;
    const label = item[labelKey] as string;

    map[id] = item;
    options.push({ id, label });
  });

  return { map, options };
};

interface IBuildStaticDropdownOptionMapProps {
  putterLevel: APIModel.PutterLevel[];
  putterType: APIModel.PutterType[];
  putterWeight: APIModel.PutterWeight[];
}

export const buildStaticDropdownOptionMap = ({ putterLevel, putterType, putterWeight }: IBuildStaticDropdownOptionMapProps) => {
  return {
    putterLevelDropdownOptionMap: buildStaticDropdown(putterLevel, 'putterLevelId', 'name'),
    putterTypeDropdownOptionMap: buildStaticDropdown(putterType, 'putterTypeId', 'name'),
    putterWeightDropdownOptionMap: buildStaticDropdown(putterWeight, 'putterWeightId', 'name'),
  };
};

interface IFilterParams {
  putterLevelIds?: string | number;
  headModelHandIds?: string | number;
  putterTypeIds?: string | number;
  putterHeadModelIds?: string | number;
}

// filter options byt any number of entity id
const matchesFilters = <T>(entity: T, filterParams: IFilterParams) => {
  let hasValidEntries = Object.entries(filterParams).every(([field, selectedId]) => {
    const allowedIds = entity[field as keyof T]; // ie: putterLevelIds: [1,2]
    let isValidId = false;
    if (field === 'putterHeadModelIds' && (entity as any).modelSelectionMode === 'AllExcept') {
      //the id MUST NOT be in the list
      isValidId = Array.isArray(allowedIds) && !allowedIds.includes(selectedId);
    } else {
      //the id MUST be in the list, OR!!! the list is actually empty and thus all Ids are allowed (this use case only happens when field is putterModelIds)
      isValidId = Array.isArray(allowedIds) && (allowedIds.includes(selectedId) || allowedIds.length === 0);
    }
    // fail if the entity selectedId is not within allowedIds
    return isValidId;
  });
  return hasValidEntries;
};

export interface IDynamicDropdownData<T> {
  map: Record<string, T>;
  // returns options filtered by multiple fields - filterParams: { allowedIdArray: selectedId }
  // eslint-disable-next-line no-unused-vars
  getOptions: (filterParams?: IFilterParams) => IDropdownOption[];
}

const buildDynamicDropdown = <T>(entities: T[], idKey: keyof T, labelKey: keyof T): IDynamicDropdownData<T> => {
  const map: Record<string, T> = {};
  const options: IDropdownOption[] = [];

  entities.forEach((item) => {
    const id = item[idKey] as string;
    const label = item[labelKey] as string;

    map[id] = item;
    options.push({ id, label });
  });

  return {
    map,
    getOptions: (filterParams?: IFilterParams) => {
      if (!filterParams) return [...options]; // return everything
      let optionsFiltered = options.filter((opt) => matchesFilters(map[opt.id], filterParams));
      return optionsFiltered;
    },
  };
};

interface IBuildDynamicDropdownOptionMapProps {
  putterFace: APIModel.PutterFaceForSaveDto[];
  putterGrip: APIModel.PutterGripForSaveDto[];
  putterHeadFinish: APIModel.HeadFinishForSaveDto[];
  putterHeadModel: APIModel.PutterHeadModelForSaveDto[];
  putterNeck: APIModel.PutterNeckForSaveDto[];
  putterShaft: APIModel.PutterShaftForSaveDto[];
  putterSightline: APIModel.PutterSightLineForSaveDto[];
}

export const buildDynamicDropdownOptionMap = ({ putterFace, putterGrip, putterHeadFinish, putterHeadModel, putterNeck, putterShaft, putterSightline }: IBuildDynamicDropdownOptionMapProps) => {
  return {
    putterFaceDropdownOptionMap: buildDynamicDropdown(putterFace, 'putterFaceId', 'name'),
    putterGripDropdownOptionMap: buildDynamicDropdown(putterGrip, 'putterGripId', 'name'),
    putterHeadFinishDropdownOptionMap: buildDynamicDropdown(putterHeadFinish, 'headFinishId', 'name'),
    putterHeadModelDropdownOptionMap: buildDynamicDropdown(putterHeadModel, 'headModelId', 'name'),
    putterNeckDropdownOptionMap: buildDynamicDropdown(putterNeck, 'putterNeckId', 'name'),
    putterShaftDropdownOptionMap: buildDynamicDropdown(putterShaft, 'putterShaftId', 'name'),
    putterSightlineDropdownOptionMap: buildDynamicDropdown(putterSightline, 'putterSightlineId', 'name'),
  };
};
